import { useState } from "react";
import {
  Home,
  Calendar,
  Heart,
  User,
  Search,
  Filter,
  ShoppingCart,
  ArrowLeft,
  Star,
  MapPin,
  Clock,
  Bell,
  Plus,
  Check,
  Activity,
  Droplets,
} from "lucide-react";

// ─── Shared micro-components ─────────────────────────────────────────────────

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full h-full">
      <div
        className="rounded-[2rem] h-full overflow-hidden"
        style={{
          background: "#1a1a2e",
          padding: "8px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.06)",
        }}
      >
        <div
          className="rounded-[1.6rem] overflow-hidden flex flex-col h-full"
          style={{ background: "#f5f8ff" }}
        >
          {/* Pill notch */}
          <div className="flex justify-center pt-2 pb-0.5 flex-shrink-0">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function StatusBar() {
  return (
    <div className="flex justify-between items-center px-3 py-0.5 text-[8px] text-gray-400 font-medium flex-shrink-0">
      <span>9:41</span>
      <span className="flex gap-0.5 items-center">
        <span className="w-2.5 h-1.5 bg-gray-400 rounded-sm" />
        <span className="w-0.5 h-1.5 bg-gray-300 rounded-sm" />
      </span>
    </div>
  );
}

// ─── Screen 1 · Home ─────────────────────────────────────────────────────────

function HomeScreen() {
  return (
    <PhoneFrame>
      <StatusBar />
      <div className="flex-1 px-2.5 pb-1 flex flex-col gap-2 overflow-hidden min-h-0">
        <div className="flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-[7px] text-gray-400">Good Morning</p>
            <p className="text-[10px] font-bold text-gray-800">Mohamed Ali</p>
          </div>
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[8px] font-bold" style={{ background: "#F5A623" }}>M</div>
        </div>

        <div className="flex items-center gap-1.5 bg-white rounded-lg px-2 py-1.5 border border-gray-100 shadow-sm flex-shrink-0">
          <Search size={8} className="text-gray-400 flex-shrink-0" />
          <span className="text-[7px] text-gray-400">Search services, doctors...</span>
        </div>

        <div className="flex-shrink-0">
          <p className="text-[8px] font-semibold text-gray-700 mb-1">Quick Actions</p>
          <div className="grid grid-cols-2 gap-1">
            {[
              { label: "Book Doctor", emoji: "🩺", bg: "#eef6ff" },
              { label: "Book Nurse", emoji: "💉", bg: "#fff0f3" },
              { label: "Caregiver", emoji: "🤝", bg: "#f0f9ff" },
              { label: "Emergency", emoji: "🚨", bg: "#fff5f5", border: "#fca5a5" },
            ].map((item) => (
              <div
                key={item.label}
                className="flex flex-col items-center justify-center gap-0.5 rounded-lg py-2"
                style={{ background: item.bg, border: `1px solid ${item.border ?? "#e5e7eb"}` }}
              >
                <span className="text-xs">{item.emoji}</span>
                <p className="text-[6.5px] font-medium text-gray-700">{item.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 min-h-0">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[8px] font-semibold text-gray-700">Upcoming Appointments</p>
            <span className="text-[6.5px] font-medium" style={{ color: "#F5A623" }}>View All</span>
          </div>
          <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-xs flex-shrink-0">🧑‍⚕️</div>
                <div>
                  <p className="text-[7px] font-semibold text-gray-800">Dr. Ahmed Hassan</p>
                  <p className="text-[6px] text-gray-400">General Physician</p>
                  <p className="text-[6.5px] font-medium" style={{ color: "#F5A623" }}>Today, 2:00 PM</p>
                </div>
              </div>
              <span className="text-[5.5px] bg-green-100 text-green-600 px-1 py-0.5 rounded-full font-semibold">confirmed</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-around items-center px-2 py-1.5 bg-white border-t border-gray-100 flex-shrink-0">
        {[
          { icon: <Home size={10} />, label: "Home", active: true },
          { icon: <Calendar size={10} />, label: "Appts" },
          { icon: <Activity size={10} />, label: "Health" },
          { icon: <User size={10} />, label: "Profile" },
        ].map((item) => (
          <div key={item.label} className="flex flex-col items-center gap-0.5">
            <span style={{ color: item.active ? "#F5A623" : "#9ca3af" }}>{item.icon}</span>
            <span className="text-[5.5px]" style={{ color: item.active ? "#F5A623" : "#9ca3af" }}>{item.label}</span>
          </div>
        ))}
      </div>
    </PhoneFrame>
  );
}

// ─── Screen 2 · Browse Services ──────────────────────────────────────────────

function BrowseScreen() {
  const doctors = [
    { name: "Dr. Ahmed Hassan", specialty: "General Physician", rating: 4.8, reviews: 156, exp: "15 yrs", price: "EGP 300/visit", badge: "Today", badgeColor: "#F5A623" },
    { name: "Dr. Sara Mohamed", specialty: "Cardiology", rating: 4.9, reviews: 203, exp: "12 yrs", price: "EGP 450/visit", badge: "Tomorrow", badgeColor: "#6366f1" },
    { name: "Nurse Fatma Ali", specialty: "Home Care Nursing", rating: 4.7, reviews: 98, exp: "8 yrs", price: "EGP 200/visit", badge: "Today", badgeColor: "#F5A623" },
  ];

  return (
    <PhoneFrame>
      <StatusBar />
      <div className="flex-1 px-2.5 pb-1 flex flex-col gap-2 overflow-hidden min-h-0">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <ArrowLeft size={10} className="text-gray-500" />
          <p className="text-[10px] font-bold text-gray-800 flex-1">Browse Services</p>
        </div>

        <div className="flex gap-1 flex-shrink-0">
          <div className="flex-1 flex items-center gap-1 bg-white rounded-lg px-2 py-1.5 border border-gray-100 shadow-sm">
            <Search size={8} className="text-gray-400" />
            <span className="text-[6.5px] text-gray-400">Search doctors...</span>
          </div>
          <div className="w-7 h-7 bg-white border border-gray-100 shadow-sm rounded-lg flex items-center justify-center">
            <Filter size={9} className="text-gray-500" />
          </div>
        </div>

        <div className="flex-shrink-0">
          <p className="text-[7px] font-semibold text-gray-500 mb-1">Specialties</p>
          <div className="flex gap-1">
            <span className="text-[6.5px] px-2 py-0.5 rounded-full text-white font-medium" style={{ background: "#F5A623" }}>All</span>
            <span className="text-[6.5px] px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-600">🩺 General (45)</span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5 flex-1 min-h-0 overflow-hidden">
          <p className="text-[7.5px] font-semibold text-gray-700 flex-shrink-0">All Professionals</p>
          {doctors.map((doc) => (
            <div key={doc.name} className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center text-xs flex-shrink-0">🧑‍⚕️</div>
                <div className="flex-1 min-w-0">
                  <p className="text-[7px] font-bold text-gray-800 truncate">{doc.name}</p>
                  <p className="text-[6px] text-gray-400">{doc.specialty}</p>
                  <div className="flex items-center gap-0.5 mt-0.5">
                    <Star size={7} className="fill-yellow-400 text-yellow-400" />
                    <span className="text-[6px] font-medium text-gray-600">{doc.rating}</span>
                    <span className="text-[6px] text-gray-400">({doc.reviews}) · {doc.exp}</span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-[6.5px] font-semibold" style={{ color: "#2C8C87" }}>{doc.price}</span>
                    <span className="text-[5.5px] px-1 py-0.5 rounded-full text-white font-medium" style={{ background: doc.badgeColor }}>{doc.badge}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PhoneFrame>
  );
}

// ─── Screen 3 · Service Details ───────────────────────────────────────────────

function ServiceDetailScreen() {
  return (
    <PhoneFrame>
      <StatusBar />
      <div className="flex-1 px-2.5 pb-1 flex flex-col gap-2 overflow-hidden min-h-0">
        <div className="flex items-center justify-between flex-shrink-0">
          <ArrowLeft size={10} className="text-gray-500" />
          <p className="text-[9px] font-bold text-gray-800">Service Details</p>
          <ShoppingCart size={10} className="text-gray-500" />
        </div>

        <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center text-xl flex-shrink-0">🧑‍⚕️</div>
            <div>
              <p className="text-[9px] font-bold text-gray-800">Dr. Ahmed Hassan</p>
              <p className="text-[7px] text-gray-400">General Physician</p>
              <div className="flex items-center gap-0.5 mt-0.5">
                {[1,2,3,4,5].map(i => <Star key={i} size={7} className="fill-yellow-400 text-yellow-400" />)}
                <span className="text-[6px] text-gray-500 ml-0.5">4.8 (156)</span>
              </div>
            </div>
          </div>
          <div className="flex justify-around mt-2 pt-2 border-t border-gray-100">
            {[
              { icon: <User size={8} />, label: "Experience", value: "15 years" },
              { icon: <MapPin size={8} />, label: "Location", value: "Home Visit" },
              { icon: <Clock size={8} />, label: "Duration", value: "30-45 min" },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-0.5">
                <span className="text-teal-500">{item.icon}</span>
                <span className="text-[5.5px] text-gray-400">{item.label}</span>
                <span className="text-[6.5px] font-semibold text-gray-700">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-shrink-0">
          <p className="text-[8px] font-semibold text-gray-700 mb-0.5">About</p>
          <p className="text-[6.5px] text-gray-500 leading-relaxed">Experienced healthcare professional providing comprehensive at-home medical services. Specialized in patient-centered care.</p>
        </div>

        <div className="flex-shrink-0">
          <p className="text-[8px] font-semibold text-gray-700 mb-1">Services Offered</p>
          <div className="flex flex-wrap gap-1">
            {["Consultation", "Diagnosis", "Treatment", "Follow-up Care"].map(s => (
              <span key={s} className="text-[6px] px-1.5 py-0.5 rounded-full border border-gray-200 bg-white text-gray-600">{s}</span>
            ))}
          </div>
        </div>

        <div className="mt-auto bg-white rounded-lg p-2 border border-gray-100 shadow-sm flex-shrink-0">
          <p className="text-[7px] text-gray-400 mb-1">Select Date</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[6px] text-gray-400">Consultation Fee</p>
              <p className="text-[10px] font-bold text-gray-800">EGP 300</p>
            </div>
            <button className="text-[7px] font-semibold px-2.5 py-1.5 rounded-lg text-white" style={{ background: "#F5A623" }}>
              Book Now
            </button>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}

// ─── Screen 4 · Health Monitoring ────────────────────────────────────────────

function MiniLineChart({ color, points, id }: { color: string; points: number[]; id: string }) {
  const max = Math.max(...points);
  const min = Math.min(...points);
  const h = 32;
  const w = 100;
  const xs = points.map((_, i) => (i / (points.length - 1)) * w);
  const ys = points.map(v => h - ((v - min) / (max - min || 1)) * (h - 4) - 2);
  const d = xs.map((x, i) => `${i === 0 ? "M" : "L"} ${x} ${ys[i]}`).join(" ");
  const fill = d + ` L ${w} ${h} L 0 ${h} Z`;

  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      <defs>
        <linearGradient id={`g-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fill} fill={`url(#g-${id})`} />
      <path d={d} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HealthScreen() {
  return (
    <PhoneFrame>
      <StatusBar />
      <div className="flex-1 px-2.5 pb-1 flex flex-col gap-2 overflow-hidden min-h-0">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <ArrowLeft size={10} className="text-gray-500" />
          <p className="text-[10px] font-bold text-gray-800 flex-1">Health Monitoring</p>
        </div>

        <div className="flex gap-0.5 bg-gray-100 rounded-full p-0.5 self-start flex-shrink-0">
          {["Day", "Week", "Month"].map(t => (
            <span key={t} className="text-[6.5px] px-2 py-0.5 rounded-full font-medium" style={t === "Week" ? { background: "#F5A623", color: "white" } : { color: "#9ca3af" }}>{t}</span>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-1 flex-shrink-0">
          {[
            { icon: "❤️", value: "72", unit: "bpm" },
            { icon: "🦶", value: "8.4K", unit: "steps" },
            { icon: "💧", value: "120/80", unit: "BP" },
          ].map((v) => (
            <div key={v.unit} className="bg-white rounded-lg p-1.5 border border-gray-100 shadow-sm flex flex-col items-center gap-0.5">
              <span className="text-xs">{v.icon}</span>
              <span className="text-[8px] font-bold text-gray-800">{v.value}</span>
              <span className="text-[5.5px] text-gray-400">{v.unit}</span>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm flex-shrink-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1">
              <Heart size={8} className="text-red-400 fill-red-400" />
              <span className="text-[7px] font-semibold text-gray-700">Heart Rate</span>
            </div>
            <span className="text-[6px] text-green-500 font-medium">↑ Normal</span>
          </div>
          <MiniLineChart id="hr" color="#ef4444" points={[65,72,68,75,71,78,72,69,74,72]} />
          <div className="flex justify-between mt-0.5">
            {["3AM","9AM","3PM","9PM"].map(t => <span key={t} className="text-[5.5px] text-gray-400">{t}</span>)}
          </div>
        </div>

        <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm flex-1 min-h-0">
          <div className="flex items-center gap-1 mb-1">
            <Droplets size={8} className="text-purple-400" />
            <span className="text-[7px] font-semibold text-gray-700">Blood Pressure</span>
          </div>
          <MiniLineChart id="bp" color="#8b5cf6" points={[120,118,122,119,121,120,123,119,121,120]} />
        </div>
      </div>
    </PhoneFrame>
  );
}

// ─── Screen 5 · Medications ───────────────────────────────────────────────────

function MedicationsScreen() {
  return (
    <PhoneFrame>
      <StatusBar />
      <div className="flex-1 px-2.5 pb-1 flex flex-col gap-2 overflow-hidden min-h-0">
        <div className="flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <ArrowLeft size={10} className="text-gray-500" />
            <p className="text-[10px] font-bold text-gray-800">Medications</p>
          </div>
          <div className="w-5 h-5 rounded-full flex items-center justify-center text-white" style={{ background: "#F5A623" }}>
            <Plus size={10} />
          </div>
        </div>

        <div className="flex-shrink-0">
          <div className="flex items-center gap-1 mb-1">
            <Bell size={8} className="text-orange-400" />
            <p className="text-[7.5px] font-semibold text-gray-700">Upcoming (2)</p>
          </div>
          {[
            { name: "Aspirin", dose: "100mg • Once daily", time: "3:00 PM" },
            { name: "Lisinopril", dose: "10mg • Once daily", time: "8:00 PM" },
          ].map((med) => (
            <div key={med.name} className="bg-orange-50 border border-orange-100 rounded-lg p-2 mb-1">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <p className="text-[7.5px] font-bold text-gray-800">{med.name}</p>
                  <p className="text-[6px] text-gray-500">{med.dose}</p>
                </div>
                <div className="flex items-center gap-0.5">
                  <Clock size={7} style={{ color: "#F5A623" }} />
                  <span className="text-[6.5px] font-semibold" style={{ color: "#F5A623" }}>{med.time}</span>
                </div>
              </div>
              <button className="w-full py-1 rounded-md text-white text-[6.5px] font-semibold" style={{ background: "#F5A623" }}>
                Mark as Taken
              </button>
            </div>
          ))}
        </div>

        <div className="flex-1 min-h-0">
          <div className="flex items-center gap-1 mb-1">
            <Check size={8} className="text-green-500" />
            <p className="text-[7.5px] font-semibold text-gray-700">Completed (2)</p>
          </div>
          {[
            { name: "Metformin", dose: "500mg • Twice daily" },
            { name: "Vitamin D", dose: "1000 IU • Once daily" },
          ].map((med) => (
            <div key={med.name} className="bg-white border border-gray-100 rounded-lg p-2 mb-1 flex items-center justify-between">
              <div>
                <p className="text-[7px] font-semibold text-gray-700">{med.name}</p>
                <p className="text-[6px] text-gray-400">{med.dose}</p>
              </div>
              <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                <Check size={8} className="text-green-500" />
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm flex-shrink-0">
          <p className="text-[7.5px] font-semibold text-gray-700 mb-1">Today's Schedule</p>
          <div className="flex items-center justify-between">
            <span className="text-[7px] text-gray-600">Aspirin</span>
            <span className="text-[7px] text-gray-400">15:00</span>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}

// ─── Main Showcase Section ────────────────────────────────────────────────────

const screens = [
  { label: "Home", component: <HomeScreen /> },
  { label: "Browse Services", component: <BrowseScreen /> },
  { label: "Service Details", component: <ServiceDetailScreen /> },
  { label: "Health Monitoring", component: <HealthScreen /> },
  { label: "Medications", component: <MedicationsScreen /> },
];

export default function AppShowcase() {
  const [active, setActive] = useState<number | null>(null);

  return (
    <section className="bg-white w-full overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 py-20 flex flex-col gap-12">

        {/* Header */}
        <div className="flex flex-col items-center text-center gap-3">
          <span
            className="text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wide"
            style={{ backgroundColor: "#b2ebf2", color: "#2C8C87" }}
          >
            App Preview
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
            Beautiful &amp; <span style={{ color: "#2C8C87" }}>Intuitive</span>
          </h2>
          <p className="text-gray-500 text-base max-w-md leading-relaxed">
            Designed for ease of use and maximum functionality
          </p>
        </div>

        {/* Phones container */}
        <div
          className="relative rounded-3xl py-10 px-4"
          style={{ background: "linear-gradient(135deg, #eef6fb 0%, #f5f8ff 50%, #fff9f0 100%)" }}
        >
          {/* Decorative blobs */}
          <div className="absolute top-4 left-8 w-32 h-32 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: "#b2ebf2" }} />
          <div className="absolute bottom-4 right-8 w-32 h-32 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: "#fde68a" }} />

          {/*
            5-column grid. Each cell has aspect-ratio 9/17 so the phone height
            scales proportionally with the column width.
            No overflow-x, no fixed widths — everything stays inside the container.
          */}
          {/*
            Extra bottom padding (pb-20) absorbs the largest translateY (36px for edge phones)
            plus label height, so nothing gets clipped.
          */}
          <div
            className="grid gap-3 w-full pb-16 pt-2"
            style={{ gridTemplateColumns: "repeat(5, 1fr)" }}
          >
            {screens.map((screen, i) => {
              const isCenter = i === 2;
              const dist = Math.abs(i - 2);
              const baseTranslateY = isCenter ? 0 : dist === 1 ? 18 : 36;
              const isHovered = active === i;
              const isDimmed = active !== null && !isHovered;

              return (
                // Outer column — no transform, just z-index
                <div
                  key={screen.label}
                  className="flex flex-col items-center"
                  style={{ position: "relative", zIndex: isHovered ? 20 : isCenter ? 10 : 5 - dist }}
                >
                  {/* Inner wrapper — phone + label move together */}
                  <div
                    onMouseEnter={() => setActive(i)}
                    onMouseLeave={() => setActive(null)}
                    className="w-full cursor-pointer flex flex-col items-center gap-4"
                    style={{
                      transform: `translateY(${isHovered ? baseTranslateY - 10 : baseTranslateY}px) scale(${isHovered ? 1.04 : 1})`,
                      opacity: isDimmed ? 0.55 : 1,
                      transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                      transformOrigin: "top center",
                    }}
                  >
                    {/* Phone */}
                    <div
                      className="w-full"
                      style={{
                        aspectRatio: "9 / 17",
                        filter: isHovered
                          ? "drop-shadow(0 28px 50px rgba(245,166,35,0.3))"
                          : "drop-shadow(0 8px 20px rgba(0,0,0,0.13))",
                        transition: "filter 0.4s ease",
                      }}
                    >
                      {screen.component}
                    </div>

                    {/* Label — travels with the phone */}
                    <span
                      className="text-xs font-medium px-3 py-1.5 rounded-full transition-all duration-300 text-center whitespace-nowrap"
                      style={
                        isHovered
                          ? { background: "#F5A623", color: "white" }
                          : { background: "rgba(0,0,0,0.07)", color: "#9ca3af" }
                      }
                    >
                      {screen.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Feature pills */}
        <div className="flex flex-wrap justify-center gap-3">
          {[
            { emoji: "🏠", text: "Home Visit Booking" },
            { emoji: "📊", text: "Health Monitoring" },
            { emoji: "💊", text: "Medication Reminders" },
            { emoji: "🚨", text: "Emergency Support" },
            { emoji: "⭐", text: "Verified Professionals" },
          ].map((pill) => (
            <div
              key={pill.text}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-400 bg-white shadow-sm text-sm text-gray-700 font-medium"
            >
              <span>{pill.emoji}</span>
              <span>{pill.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}